import { snapdom } from "@zumer/snapdom";

import canvasPlugin from "components/screenshot/plugins/canvasPlugin";
import legendTitleWidthPlugin from "components/screenshot/plugins/legendTitleWidthPlugin";
import virtuosoScrollPlugin from "components/screenshot/plugins/virtuosoScrollPlugin";

interface CaptureOptions {
  embedFonts?: boolean;
}

/** Hide border/outline on an overlay element before capture. */
const hideOverlayBorders = (overlay: HTMLDivElement): void => {
  overlay.style.border = "none";
  overlay.style.outline = "none";
};

/**
 * Capture an element to a PNG Blob using snapdom with canvas preservation.
 *
 * Uses canvasPlugin to snapshot all <canvas> elements (ECharts, Mapbox,
 * Three.js, etc.) from the live DOM before cloning — prevents blank
 * canvases on Windows where Chrome flushes the GPU backing store.
 */
export const captureToBlob = async (
  element: HTMLElement,
  { embedFonts = true }: CaptureOptions = {}
): Promise<Blob | null> =>
  snapdom.toBlob(element, {
    type: "png",
    embedFonts,
    plugins: [canvasPlugin, virtuosoScrollPlugin, legendTitleWidthPlugin]
  });

/**
 * Capture a DOM element selected by CSS selector and copy to clipboard.
 *
 * Shared implementation for chart, MPC, and SAM widget screenshot actions.
 * Optionally hides the screenshot overlay borders before capture.
 */
export const captureElementToClipboard = async (
  selector: string,
  screenshotOverlay?: HTMLDivElement
): Promise<void> => {
  if (screenshotOverlay) hideOverlayBorders(screenshotOverlay);

  const element = document.querySelector(selector) as HTMLElement;
  const blob = await captureToBlob(element);

  if (!blob || !navigator.clipboard) return;

  await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
};

/** Resolve the correct ChartPlot selector based on fullscreen state. */
export const getChartPlotSelector = (): string =>
  document.querySelector(".fullscreen .ChartPlot")
    ? ".fullscreen .ChartPlot"
    : ".App .chart-wrapper > div > .ChartPlot";
